import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import Select from '@cloudscape-design/components/select';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { fetchJobs, JobListItem, JobStatus } from '../services/api';
import AgentBadge from './AgentBadge';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Awaiting review', value: 'awaiting-review' },
  { label: 'Manual review required', value: 'manual-review-required' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Processing', value: 'processing' },
  { label: 'Completed', value: 'completed' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Failed', value: 'failed' },
];

export function statusIndicatorType(status: JobStatus): 'success' | 'warning' | 'error' | 'info' | 'pending' {
  switch (status) {
    case 'approved':
    case 'completed': return 'success';
    case 'awaiting-review':
    case 'manual-review-required': return 'warning';
    case 'rejected':
    case 'failed': return 'error';
    case 'processing': return 'info';
    default: return 'pending';
  }
}

const JobList: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params: { status?: JobStatus; limit: number; offset: number } = {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
    };
    if (statusFilter) params.status = statusFilter as JobStatus;

    fetchJobs(params)
      .then(data => { setJobs(data.jobs); setTotal(data.total); })
      .catch(() => { setJobs([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [currentPage, statusFilter]);

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Review and manage broker migration jobs">
        Migration Jobs
      </Header>

      <Select
        selectedOption={STATUS_OPTIONS.find(o => o.value === statusFilter) || STATUS_OPTIONS[0]}
        onChange={({ detail }: { detail: { selectedOption: { value?: string } } }) => { setStatusFilter(detail.selectedOption.value ?? ''); setCurrentPage(1); }}
        options={STATUS_OPTIONS}
        placeholder="Filter by status"
      />

      <Table
        items={jobs}
        loading={loading}
        columnDefinitions={[
          { id: 'jobId', header: 'Job ID', cell: (item: JobListItem) => item.jobId },
          { id: 'brokerName', header: 'Broker', cell: (item: JobListItem) => item.brokerName },
          {
            id: 'status',
            header: 'Status',
            cell: (item: JobListItem) => (
              <StatusIndicator type={statusIndicatorType(item.status)}>{item.status}</StatusIndicator>
            ),
          },
          { id: 'createdAt', header: 'Created', cell: (item: JobListItem) => new Date(item.createdAt).toLocaleString() },
          {
            id: 'template',
            header: 'Template',
            cell: (item: JobListItem) => {
              const info = item.templateMatchInfo;
              if (!info) return '—';
              const pct = Math.round(info.similarity * 100);
              if (pct < 1) return '—';
              return <Badge color="green">{pct}%</Badge>;
            },
          },
          {
            id: 'submissions',
            header: 'Submissions',
            cell: (item: JobListItem) => {
              const p = item.submissionProgress;
              if (!p || p.total === 0) return '—';
              const color = p.submitted === p.total ? 'green'
                : p.failed > 0 ? 'red'
                : 'blue';
              return (
                <Badge color={color}>
                  {p.submitted}/{p.total}
                  {p.failed > 0 ? ` (${p.failed} failed)` : ''}
                </Badge>
              );
            },
          },
          {
            id: 'duration',
            header: 'Duration',
            cell: (item: JobListItem) => {
              if (item.processingTimeMs == null) return '—';
              const secs = item.processingTimeMs / 1000;
              if (secs < 60) return `${secs.toFixed(1)}s`;
              const mins = Math.floor(secs / 60);
              const remSecs = Math.round(secs % 60);
              return `${mins}m ${remSecs}s`;
            },
          },
          {
            id: 'agent',
            header: 'Agent',
            cell: (item: JobListItem) => <AgentBadge processedBy={item.processedBy} />,
          },
        ]}
        onRowClick={({ detail }: { detail: { item: JobListItem } }) => navigate(`/jobs/${detail.item.jobId}`)}
        empty="No jobs found"
        pagination={
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={Math.ceil(total / PAGE_SIZE) || 1}
            onChange={({ detail }: { detail: { currentPageIndex: number } }) => setCurrentPage(detail.currentPageIndex)}
          />
        }
      />
    </SpaceBetween>
  );
};

export default JobList;
